import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslocoModule } from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';

@Component({
  selector: 'anihis-lab-hemato-bio-cat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
  ],
  templateUrl: './lab-hemato-bio-cat.component.html',
  styleUrls: ['./lab-hemato-bio-cat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabHematoBioCatComponent
  extends FormBaseComponent
  implements OnInit
{
  @Output() formChange = new EventEmitter<any>();
  leftParams: Array<{ labelKey: string; control: string }> = [];
  rightParams: Array<{ labelKey: string; control: string }> = [];
  unitMap: Record<string, string> = {};
  refMap: Record<string, string> = {};

  constructor() {
    super();
    this.form = this.fb.group({
      // Hematology
      wbc: new FormControl(''),
      lymphCount: new FormControl(''),
      monCount: new FormControl(''),
      granCount: new FormControl(''),
      lymphPercent: new FormControl(''),
      monPercent: new FormControl(''),
      granPercent: new FormControl(''),
      rbc: new FormControl(''),
      hgb: new FormControl(''),
      hct: new FormControl(''),
      mcv: new FormControl(''),
      mch: new FormControl(''),
      mchc: new FormControl(''),
      rdw: new FormControl(''),
      plt: new FormControl(''),
      mpv: new FormControl(''),
      pdw: new FormControl(''),
      pct: new FormControl(''),
      eos: new FormControl(''),
      // Biochemistry
      alt: new FormControl(''),
      ast: new FormControl(''),
      alp: new FormControl(''),
      ggt: new FormControl(''),
      ck: new FormControl(''),
      ldh: new FormControl(''),
      amylase: new FormControl(''),
      glucose: new FormControl(''),
      totalProteins: new FormControl(''),
      albumin: new FormControl(''),
      globulins: new FormControl(''),
      agRatio: new FormControl(''),
      urea: new FormControl(''),
      creatinine: new FormControl(''),
      ureaCreatinineRatio: new FormControl(''),
      bilirubin: new FormControl(''),
      triglycerides: new FormControl(''),
      cholesterol: new FormControl(''),
      calcium: new FormControl(''),
      phosphorus: new FormControl(''),
      potassium: new FormControl(''),
      sodium: new FormControl(''),
      chloride: new FormControl(''),
      magnesium: new FormControl(''),
      iron: new FormControl(''),
      // Blood smear
      bloodSmear: new FormControl(''),
      veterinarianName: new FormControl(''),
    });
    // define parameter lists for template rendering
    this.leftParams = [
      { labelKey: 'WBC', control: 'wbc' },
      { labelKey: 'Lymph#', control: 'lymphCount' },
      { labelKey: 'Mon#', control: 'monCount' },
      { labelKey: 'Gran#', control: 'granCount' },
      { labelKey: 'Lymph%', control: 'lymphPercent' },
      { labelKey: 'Mon%', control: 'monPercent' },
      { labelKey: 'Gran%', control: 'granPercent' },
      { labelKey: 'RBC', control: 'rbc' },
      { labelKey: 'HGB', control: 'hgb' },
      { labelKey: 'HCT', control: 'hct' },
      { labelKey: 'MCV', control: 'mcv' },
      { labelKey: 'MCH', control: 'mch' },
      { labelKey: 'MCHC', control: 'mchc' },
      { labelKey: 'RDW', control: 'rdw' },
      { labelKey: 'PLT', control: 'plt' },
      { labelKey: 'MPV', control: 'mpv' },
      { labelKey: 'PDW', control: 'pdw' },
      { labelKey: 'PCT', control: 'pct' },
      { labelKey: 'EOS', control: 'eos' },
    ];

    this.rightParams = [
      { labelKey: 'ALT', control: 'alt' },
      { labelKey: 'AST', control: 'ast' },
      { labelKey: 'ALP', control: 'alp' },
      { labelKey: 'GGT', control: 'ggt' },
      { labelKey: 'CK', control: 'ck' },
      { labelKey: 'LDH', control: 'ldh' },
      { labelKey: 'Alfa-amylaza', control: 'amylase' },
      { labelKey: 'Glukoza', control: 'glucose' },
      { labelKey: 'Uk.proteini', control: 'totalProteins' },
      { labelKey: 'Albumini', control: 'albumin' },
      { labelKey: 'Globulini', control: 'globulins' },
      { labelKey: 'Alb/glob', control: 'agRatio' },
      { labelKey: 'Urea', control: 'urea' },
      { labelKey: 'Kreatinin', control: 'creatinine' },
      { labelKey: 'Urea/ Kreatinin', control: 'ureaCreatinineRatio' },
      { labelKey: 'Uk.bilirubin', control: 'bilirubin' },
      { labelKey: 'Trigliceridi', control: 'triglycerides' },
      { labelKey: 'Holesterol', control: 'cholesterol' },
      { labelKey: 'Kalcijum', control: 'calcium' },
      { labelKey: 'Fosfor', control: 'phosphorus' },
      { labelKey: 'Kalijum', control: 'potassium' },
      { labelKey: 'Natrijum', control: 'sodium' },
      { labelKey: 'Hloridi', control: 'chloride' },
      { labelKey: 'Magnezijum', control: 'magnesium' },
      { labelKey: 'Gvožđe', control: 'iron' },
    ];

    // units and reference ranges for controls (cat values taken from provided table)
    this.unitMap = {
      // Hematology
      wbc: 'x10^9/L',
      lymphCount: 'x10^9/L',
      monCount: 'x10^9/L',
      granCount: 'x10^9/L',
      lymphPercent: '%',
      monPercent: '%',
      granPercent: '%',
      rbc: 'x10^9/L',
      hgb: 'g/L',
      hct: '%',
      mcv: 'fL',
      mch: 'pg',
      mchc: 'g/L',
      rdw: '%',
      plt: 'x10^9/L',
      mpv: 'fL',
      pdw: '',
      pct: '%',
      eos: '%',
      // Biochemistry
      alt: 'IU/L',
      ast: 'IU/L',
      alp: 'IU/L',
      ggt: 'IU/L',
      ck: 'IU/L',
      ldh: 'IU/L',
      amylase: 'IU/L',
      glucose: 'mmol/L',
      totalProteins: 'g/L',
      albumin: 'g/L',
      globulins: 'g/L',
      agRatio: '',
      urea: 'mmol/L',
      creatinine: 'μmol/L',
      ureaCreatinineRatio: '',
      bilirubin: 'μmol/L',
      triglycerides: 'mmol/L',
      cholesterol: 'mmol/L',
      calcium: 'mmol/L',
      phosphorus: 'mmol/L',
      potassium: 'mmol/L',
      sodium: 'mmol/L',
      chloride: 'mmol/L',
      magnesium: 'mmol/L',
      iron: 'μmol/L',
    };

    this.refMap = {
      // Hematology
      wbc: '5.5 - 19.0',
      lymphCount: '0.8 - 7.0',
      monCount: '0.0 - 1.9',
      granCount: '2.1 - 15.0',
      lymphPercent: '12.0 - 45.0',
      monPercent: '2.0 - 9.0',
      granPercent: '35.0 - 85.0',
      rbc: '4.6 - 10.0',
      hgb: '93 - 153',
      hct: '28.0 - 49.0',
      mcv: '39.0 - 52.0',
      mch: '13.0 - 21.0',
      mchc: '300 - 380',
      rdw: '14.0 - 18.0',
      plt: '100 - 514',
      mpv: '5.0 - 11.8',
      pdw: '',
      pct: '',
      eos: '',
      // Biochemistry
      alt: '8.3 - 52.5',
      ast: '9.2 - 30.0',
      alp: '12.0 - 65.1',
      ggt: '1.8 - 12.0',
      ck: '17 - 130',
      ldh: '35.1 - 224.9',
      amylase: '371.3 - 2331.0',
      glucose: '2.7 - 6.3',
      totalProteins: '57 - 94',
      albumin: '26 - 56',
      globulins: '24 - 47',
      agRatio: '0.35 - 1.5',
      urea: '5.5 - 11.1',
      creatinine: '0 - 167',
      ureaCreatinineRatio: '40 - 100',
      bilirubin: '1.71 - 8.5',
      triglycerides: '0 - 0.6',
      cholesterol: '1.8 - 3.4',
      calcium: '2.3 - 3.0',
      phosphorus: '1.3 - 2.3',
      potassium: '3.8 - 5.3',
      sodium: '145 - 158',
      chloride: '110 - 130',
      magnesium: '1.5 - 3.0',
      iron: '13 - 36',
    };
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.subs.sink = this.form.valueChanges.subscribe((v) =>
      this.formChange.emit(v),
    );
  }
}
